// src/hooks/useFiscalDataTracking.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useRef, useEffect, useCallback } from 'react';

// *******************************************************************************
// HOOK
// *******************************************************************************
/**
 * Custom hook to track fiscal data changes for change detection
 * Used by CompaniesDocument and SubscriptionsDocument to detect when fiscal
 * services are being activated (false → true) or when fields have changed.
 *
 * @param {Object} data - Document data from DocumentContext
 * @returns {Object} - { savedFiscalDataRef, updateSavedFiscalData, getSavedFiscalData }
 */
const useFiscalDataTracking = (data) => {
  // Track the last saved fiscalData to compare for changes
  // This is needed because `data` from DocumentProvider doesn't update after save
  const savedFiscalDataRef = useRef(null);

  // Initialize savedFiscalDataRef when document is loaded from DB
  // This captures the "original" state to compare against for change detection
  useEffect(() => {
    // Only initialize if we have meaningful fiscal data (at least one key with a value)
    const hasMeaningfulData = data?.fiscalData && (
      data.fiscalData.taxRegime ||
      data.fiscalData.stateRegistration ||
      data.fiscalData.nfce?.isEnabled !== undefined ||
      data.fiscalData.nfe?.isEnabled !== undefined ||
      data.fiscalData.nfse?.isEnabled !== undefined
    );

    if (hasMeaningfulData && !savedFiscalDataRef.current) {
      // Deep clone to avoid reference issues
      savedFiscalDataRef.current = JSON.parse(JSON.stringify(data.fiscalData));
    }
  }, [data?.fiscalData]);

  /**
   * Update the saved fiscal data ref after a successful save
   * Call this in handleFinish after the save completes successfully
   * @param {Object} fiscalData - The fiscal data that was just saved
   */
  const updateSavedFiscalData = useCallback((fiscalData) => {
    if (fiscalData) {
      savedFiscalDataRef.current = JSON.parse(JSON.stringify(fiscalData));
    }
  }, []);

  /**
   * Get the current saved fiscal data (for comparison)
   * Falls back to data?.fiscalData if no saved data yet
   * @returns {Object} - The saved fiscal data or fallback
   */
  const getSavedFiscalData = useCallback(() => {
    return savedFiscalDataRef.current || data?.fiscalData || {};
  }, [data?.fiscalData]);

  return {
    savedFiscalDataRef,
    updateSavedFiscalData,
    getSavedFiscalData,
  };
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default useFiscalDataTracking;
